import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  IconButton,
  Typography,
  Tooltip,
  Stack,
  TextField,
  Popover,
  Button,
} from '@mui/material';
import ReplyIcon from '@mui/icons-material/Reply';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import EmojiEmotionsIcon from '@mui/icons-material/EmojiEmotions';
import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import ThumbUpAltOutlinedIcon from '@mui/icons-material/ThumbUpAltOutlined';
import ThumbDownAltOutlinedIcon from '@mui/icons-material/ThumbDownAltOutlined';

const QUICK_EMOJIS = ['👍', '❤️', '😂', '😮', '😢', '👏', '🔥', '✅'];

/**
 * 메시지 버블 전체를 감싸는 hover 컨테이너
 * 버블 + 액션바를 하나의 hover 영역으로 묶어서 gap 문제 원천 차단
 */
export const MessageHoverWrapper = ({
  children,
  message,
  isMe,
  isBot,
  canEdit,
  canDelete,
  onReply,
  onEdit,
  onDelete,
  onReact,
}) => {
  const [hovered, setHovered] = useState(false);
  const [emojiAnchor, setEmojiAnchor] = useState(null);
  const leaveTimer = useRef(null);

  const handleMouseEnter = () => {
    clearTimeout(leaveTimer.current);
    setHovered(true);
  };
  const handleMouseLeave = () => {
    leaveTimer.current = setTimeout(() => setHovered(false), 80);
  };

  return (
    <Box
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      sx={{ position: 'relative', maxWidth: '100%' }}
    >
      {/* 액션 툴바 */}
      {!isBot && hovered && (
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: 'absolute',
            top: -48,
            right: isMe ? 0 : 'auto',
            left: isMe ? 'auto' : 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 2.5,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            px: 1,
            py: 0.75,
            gap: 0.5,
          }}
        >
          {onReact && (
            <Tooltip title="이모지 반응" placement="top">
              <IconButton
                sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                  '&:hover': { color: '#ffc107', bgcolor: 'rgba(255,193,7,0.1)' } }}
                onClick={(e) => { e.stopPropagation(); setEmojiAnchor(e.currentTarget); }}
              >
                <EmojiEmotionsIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          <Tooltip title="답장" placement="top">
            <IconButton
              sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                '&:hover': { color: '#1976d2', bgcolor: 'rgba(25,118,210,0.1)' } }}
              onClick={(e) => { e.stopPropagation(); onReply && onReply(message); }}
            >
              <ReplyIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>

          {canEdit && (
            <Tooltip title="수정" placement="top">
              <IconButton
                sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                  '&:hover': { color: '#1976d2', bgcolor: 'rgba(25,118,210,0.1)' } }}
                onClick={(e) => { e.stopPropagation(); onEdit && onEdit(message); }}
              >
                <EditIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}

          {canDelete && (
            <Tooltip title="삭제" placement="top">
              <IconButton
                sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                  '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.1)' } }}
                onClick={(e) => { e.stopPropagation(); onDelete && onDelete(message); }}
              >
                <DeleteIcon sx={{ fontSize: 20 }} />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      )}

      {isBot && hovered && onReact && (
        <Box
          onMouseEnter={handleMouseEnter}
          onMouseLeave={handleMouseLeave}
          sx={{
            position: 'absolute',
            top: -42,
            left: 0,
            zIndex: 20,
            display: 'flex',
            alignItems: 'center',
            bgcolor: '#fff',
            border: '1px solid #e0e0e0',
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            px: 0.75,
            py: 0.5,
            gap: 0.5,
          }}
        >
          <Tooltip title="좋은 응답" placement="top">
            <IconButton
              sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                '&:hover': { color: '#2e7d32', bgcolor: 'rgba(46,125,50,0.1)' } }}
              onClick={(e) => { e.stopPropagation(); onReact(message, '👍'); }}
            >
              <ThumbUpAltOutlinedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
          <Tooltip title="개선 필요" placement="top">
            <IconButton
              sx={{ p: 0.75, color: '#9e9e9e', borderRadius: 1.5,
                '&:hover': { color: '#d32f2f', bgcolor: 'rgba(211,47,47,0.1)' } }}
              onClick={(e) => { e.stopPropagation(); onReact(message, '👎'); }}
            >
              <ThumbDownAltOutlinedIcon sx={{ fontSize: 19 }} />
            </IconButton>
          </Tooltip>
        </Box>
      )}

      {/* 버블 본체 */}
      {children}

      {/* 이모지 피커 */}
      <Popover
        open={Boolean(emojiAnchor)}
        anchorEl={emojiAnchor}
        onClose={() => setEmojiAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: isMe ? 'right' : 'left' }}
        transformOrigin={{ vertical: 'bottom', horizontal: isMe ? 'right' : 'left' }}
        PaperProps={{
          sx: { borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.15)', border: '1px solid #e0e0e0', p: 1 },
        }}
      >
        <Stack direction="row" spacing={0.5}>
          {QUICK_EMOJIS.map((emoji) => (
            <Box
              key={emoji}
              onClick={() => { onReact && onReact(message, emoji); setEmojiAnchor(null); }}
              sx={{
                fontSize: '1.2rem', cursor: 'pointer', p: 0.5, borderRadius: 1,
                transition: 'transform 0.1s',
                '&:hover': { transform: 'scale(1.3)', bgcolor: '#f5f5f5' },
              }}
            >
              {emoji}
            </Box>
          ))}
        </Stack>
      </Popover>
    </Box>
  );
};

// 하위 호환용
export const MessageActionBar = () => null;

export const InlineEditInput = ({ originalContent, onSave, onCancel }) => {
  const initialValue = originalContent || '';
  const [value, setValue] = useState(initialValue);
  const inputRef = useRef();

  useEffect(() => {
    inputRef.current?.focus();
    const len = initialValue.length;
    inputRef.current?.setSelectionRange(len, len);
  }, [initialValue]);

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      if (value.trim() && value.trim() !== originalContent) onSave(value.trim());
      else onCancel();
    }
    if (e.key === 'Escape') onCancel();
  };

  return (
    <Box sx={{ width: '100%' }}>
      <TextField
        inputRef={inputRef}
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        multiline fullWidth size="small" variant="outlined"
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#fff', borderRadius: 2, fontSize: '0.875rem',
            '& fieldset': { borderColor: '#1976d2', borderWidth: 2 },
          },
        }}
      />
      <Stack direction="row" spacing={0.5} sx={{ mt: 0.75, justifyContent: 'flex-end' }}>
        <Typography variant="caption" sx={{ color: '#9e9e9e', alignSelf: 'center', mr: 1 }}>
          Enter로 저장, Esc로 취소
        </Typography>
        <Button size="small" variant="outlined" onClick={onCancel}
          startIcon={<CloseIcon sx={{ fontSize: 14 }} />}
          sx={{ fontSize: '0.72rem', py: 0.25, px: 1, textTransform: 'none' }}>
          취소
        </Button>
        <Button size="small" variant="contained"
          onClick={() => value.trim() && value.trim() !== originalContent && onSave(value.trim())}
          startIcon={<CheckIcon sx={{ fontSize: 14 }} />}
          disabled={!value.trim() || value.trim() === originalContent}
          sx={{ fontSize: '0.72rem', py: 0.25, px: 1, textTransform: 'none' }}>
          저장
        </Button>
      </Stack>
    </Box>
  );
};

export const ReplyPreviewBanner = ({ replyTarget, onCancel }) => {
  if (!replyTarget) return null;
  return (
    <Box sx={{
      mx: 1.5, mb: 0.5, px: 1.5, py: 0.75,
      bgcolor: '#e8f4fd', borderRadius: '8px 8px 0 0',
      borderLeft: '3px solid #1976d2',
      display: 'flex', alignItems: 'flex-start', gap: 1,
    }}>
      <ReplyIcon sx={{ fontSize: 16, color: '#1976d2', mt: 0.25, flexShrink: 0 }} />
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#1976d2' }}>
          {replyTarget.usrNm || replyTarget.crtNm}에게 답장
        </Typography>
        <Typography noWrap sx={{ fontSize: '0.78rem', color: '#555', mt: 0.125 }}>
          {replyTarget.content || replyTarget.text || '(파일)'}
        </Typography>
      </Box>
      <IconButton size="small" onClick={onCancel}
        sx={{ p: 0.25, color: '#9e9e9e', '&:hover': { color: '#333' } }}>
        <CloseIcon sx={{ fontSize: 14 }} />
      </IconButton>
    </Box>
  );
};

export const ReplyQuote = ({ replyToMessage, isMe, onClickQuote }) => {
  if (!replyToMessage) return null;
  return (
    <Box
      onClick={() => onClickQuote?.(replyToMessage.messageUuid)}
      sx={{
        mb: 0.75, px: 1, py: 0.5,
        bgcolor: isMe ? 'rgba(255,255,255,0.15)' : 'rgba(0,0,0,0.05)',
        borderRadius: 1.5, borderLeft: '3px solid',
        borderLeftColor: isMe ? 'rgba(255,255,255,0.5)' : '#1976d2',
        cursor: 'pointer',
        transition: 'opacity 0.15s',
        '&:hover': { opacity: 0.75 },
      }}>
      <Typography sx={{
        fontSize: '0.7rem', fontWeight: 700, mb: 0.25,
        color: isMe ? 'rgba(255,255,255,0.8)' : '#1976d2',
      }}>
        {replyToMessage.usrNm || replyToMessage.crtNm}
      </Typography>
      <Typography noWrap sx={{ fontSize: '0.75rem', color: isMe ? 'rgba(255,255,255,0.7)' : '#666' }}>
        {replyToMessage.content || replyToMessage.text || '(파일)'}
      </Typography>
    </Box>
  );
};

export const EmojiReactions = ({ reactions, onReact, currentUser }) => {
  if (!reactions || reactions.length === 0) return null;

  const grouped = reactions.reduce((acc, r) => {
    const existing = acc.find((g) => g.emoji === r.emoji);
    if (existing) {
      existing.users.push(r.usrNm || r.usrUuid);
      existing.count++;
      if (r.usrUuid === currentUser) existing.isMine = true;
    } else {
      acc.push({ emoji: r.emoji, users: [r.usrNm || r.usrUuid], count: 1, isMine: r.usrUuid === currentUser });
    }
    return acc;
  }, []);

  return (
    <Stack direction="row" spacing={0.5} flexWrap="wrap" sx={{ mt: 0.5 }}>
      {grouped.map((g) => (
        <Tooltip key={g.emoji} title={g.users.join(', ')} placement="top">
          <Box
            onClick={() => onReact && onReact(g.emoji)}
            sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 0.75, py: 0.25, borderRadius: 3, cursor: 'pointer',
              border: '1px solid', borderColor: g.isMine ? '#1976d2' : '#e0e0e0',
              bgcolor: g.isMine ? '#e3f2fd' : '#f5f5f5',
              fontSize: '0.8rem', lineHeight: 1, transition: 'all 0.15s',
              '&:hover': { transform: 'scale(1.1)', boxShadow: '0 2px 8px rgba(0,0,0,0.1)' },
            }}
          >
            <span>{g.emoji}</span>
            {g.count > 1 && (
              <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#555' }}>{g.count}</Typography>
            )}
          </Box>
        </Tooltip>
      ))}
    </Stack>
  );
};

export default MessageHoverWrapper;
